import { useTranslation } from '@silospot/i18n/react';
import { useQuery } from '@tanstack/react-query';
import type { ReactElement } from 'react';
import { useParams } from 'react-router-dom';
import { Card, PageHeader } from '../../components/cld';
import { api } from '../../lib/api';
import { fmtDateTime } from '../../lib/datetime';

type Details = Record<string, unknown> | null;
type Tr = (key: string, vars?: Record<string, string>) => string;

/**
 * Turn one audit entry into a single plain-language sentence — never raw JSON and
 * never an internal/technical term (no provider names, no ids, no "zaraz"). The
 * stored payload is read-only; we only format what already exists for the client.
 */
function describeAction(action: string, details: Details, t: Tr): string {
  const d = (details ?? {}) as Record<string, unknown>;
  const s = (k: string): string => (typeof d[k] === 'string' ? (d[k] as string) : '');
  switch (action) {
    case 'project.created': {
      const name = s('name') || s('domain');
      return name ? t('log.eProjectCreated', { name }) : t('log.eProjectCreatedNoName');
    }
    case 'project.deleted': {
      // The deletion record carries only an id (+ internal cleanup info we never
      // surface). Show the site name only if it happens to be present.
      const name = s('name') || s('domain');
      return name ? t('log.eProjectDeletedNamed', { name }) : t('log.eProjectDeleted');
    }
    case 'project.restored': {
      const name = s('name') || s('domain');
      return name ? t('log.eProjectRestored', { name }) : t('log.eGeneric');
    }
    case 'project.purged': {
      const name = s('name') || s('domain');
      return name ? t('log.eProjectPurged', { name }) : t('log.eGeneric');
    }
    case 'blocklist.prepare': {
      const ip = s('ip');
      const scope = d.scope === 'all' ? t('log.scopeAll') : t('log.scopeSite');
      const base =
        d.action === 'block'
          ? t('log.eBlockBlocked', { ip, scope })
          : t('log.eBlockChallenged', { ip, scope });
      const reason = s('reason');
      return reason ? base + t('log.eBlockReason', { reason }) : base;
    }
    case 'blocklist.remove':
      return t('log.eUnblocked', { ip: s('ip') });
    case 'verdict.create': {
      const labels: Record<string, string> = {
        false_positive: t('log.vFalsePositive'),
        real_client: t('log.vRealClient'),
        confirmed_fraud: t('log.vConfirmedFraud'),
        internal_test: t('log.vInternalTest'),
      };
      const label = labels[s('verdict')];
      return label ? t('log.eVerdict', { verdict: label }) : t('log.eGeneric');
    }
    default:
      // Unknown/new action type: a clean generic label — never the raw type or JSON.
      return t('log.eGeneric');
  }
}

export function LogPage(): ReactElement {
  const { t } = useTranslation('pages');
  const { t: ts } = useTranslation('shell');
  const { id = '' } = useParams();
  const { data, isLoading } = useQuery({
    queryKey: ['cld-log', id],
    queryFn: () => api.getLog(id),
    enabled: !!id,
  });

  const header = <PageHeader title={ts('nav.log')} sub={t('log.intro')} />;
  if (isLoading || !data) {
    return (
      <div>
        {header}
        <Card>
          <div className="h-48 animate-pulse rounded-lg bg-neutral-bg" />
        </Card>
      </div>
    );
  }

  return (
    <div>
      {header}
      <Card className="p-0">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-[13px]">
            <thead>
              <tr>
                {[t('log.date'), t('log.action')].map((h) => (
                  <th
                    key={h}
                    className="whitespace-nowrap border-b border-border px-4 py-3 text-left text-[11px] font-semibold uppercase tracking-[0.04em] text-text-3"
                  >
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {data.rows.map((r) => (
                <tr key={r.id} className="hover:bg-[#fafbfc]">
                  <td className="whitespace-nowrap border-b border-[#f1f3f6] px-4 py-3 font-mono text-[12px] text-text-3">
                    {fmtDateTime(r.occurredAt)}
                  </td>
                  <td className="border-b border-[#f1f3f6] px-4 py-3 text-text-2">
                    {describeAction(r.action, r.details, t)}
                  </td>
                </tr>
              ))}
              {data.rows.length === 0 ? (
                <tr>
                  <td colSpan={2} className="px-4 py-12 text-center text-[13px] text-text-3">
                    {t('log.empty')}
                  </td>
                </tr>
              ) : null}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}
